import { useRef, useState, useLayoutEffect, useCallback } from 'react';
import type { MouseEventHandler, ReactElement } from 'react';
import type { ReactNode } from 'react';

import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { useElementDimensions } from 'src/hooks/use-element-dimensions';

import { Label } from '../label';

import type { LabelColor, LabelVariant } from '../label';

const CHIP_LAYOUT_SX: SxProps<Theme> = {
  height: 20,
  minHeight: 20,
  maxHeight: 20,
  display: 'inline-flex',
  alignItems: 'center',
};

const CHIP_TRUNCATION_SX: SxProps<Theme> = {
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CHIP_SX: SxProps<Theme> = {
  ...CHIP_LAYOUT_SX,
  ...CHIP_TRUNCATION_SX,
};

const LABEL_SX: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

const OVERFLOW_CHIP_SX: SxProps<Theme> = {
  ...CHIP_LAYOUT_SX,
};

export interface Tag {
  id: string | number;
  label: string;
  color?: LabelColor;
  variant?: LabelVariant;
}

const TOOLTIP_MAX_WIDTH = 360;
const TOOLTIP_MAX_HEIGHT = 280;

const TOOLTIP_SCROLL_SX: SxProps<Theme> = {
  p: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 0.5,
  maxHeight: TOOLTIP_MAX_HEIGHT,
  overflowY: 'auto',
  overflowX: 'hidden',
  width: 'max-content',
  maxWidth: TOOLTIP_MAX_WIDTH,
  minWidth: 0,
  boxSizing: 'border-box',
};

const TOOLTIP_LABEL_SX: SxProps<Theme> = {
  display: 'inline-flex',
  alignSelf: 'flex-start',
  width: 'auto',
  maxWidth: '100%',
  height: 'auto',
  maxHeight: 'none',
  lineHeight: 1.4,
  alignItems: 'flex-start',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflow: 'visible',
  textOverflow: 'unset',
};

export function TagsListTooltipContent({
  tags,
  color,
  variant,
}: {
  tags: Tag[];
  color?: LabelColor;
  variant?: LabelVariant;
}) {
  return (
    <Box sx={TOOLTIP_SCROLL_SX}>
      {tags.map((tag, index) => (
        <Label
          key={`tooltip-${String(tag.id)}-${index}`}
          variant={tag.variant ?? variant ?? 'soft'}
          color={tag.color ?? color ?? 'default'}
          sx={TOOLTIP_LABEL_SX}
        >
          {tag.label}
        </Label>
      ))}
    </Box>
  );
}

const WRAPPED_LABEL_SX: SxProps<Theme> = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.4,
  overflow: 'visible',
  textOverflow: 'unset',
};

const WRAPPED_CHIP_SX: SxProps<Theme> = {
  ...CHIP_LAYOUT_SX,
  height: 'auto',
  minHeight: 20,
  maxHeight: 'none',
  maxWidth: '100%',
  alignItems: 'flex-start',
};

interface Props {
  containerIdentifier: string;
  containerHeight?: number;
  tags: Tag[];
  color?: LabelColor;
  variant?: LabelVariant;
  overflowColor?: LabelColor;
  overflowVariant?: LabelVariant;
  showTooltip?: boolean;
  /** Show every chip with wrapping; no +N overflow or nested tooltip. */
  wrap?: boolean;
  tooltipEnterDelayMs?: number;
  onRootClickCapture?: MouseEventHandler<HTMLDivElement>;
  chipSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  overflowChipSx?: SxProps<Theme>;
  tooltipContent?: ReactNode;
}

function TagsListWrapped({
  tags,
  color,
  variant,
  onRootClickCapture,
  chipSx,
  labelSx,
}: Pick<Props, 'tags' | 'color' | 'variant' | 'onRootClickCapture' | 'chipSx' | 'labelSx'>) {
  const resolvedWrappedChipSx = { ...WRAPPED_CHIP_SX, ...chipSx };
  const resolvedWrappedLabelSx = { ...WRAPPED_LABEL_SX, ...labelSx };

  return (
    <Box
      onClickCapture={onRootClickCapture}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        width: '100%',
        minWidth: 0,
      }}
    >
      {tags.map((c, i) => (
        <Label
          key={`chip-${String(c.id)}-${i}`}
          variant={c.variant ?? variant ?? 'soft'}
          color={c.color ?? color ?? 'default'}
          sx={resolvedWrappedChipSx}
        >
          <Box component="span" sx={resolvedWrappedLabelSx}>
            {c.label}
          </Box>
        </Label>
      ))}
    </Box>
  );
}

function TagsListOverflow({
  containerIdentifier,
  tags,
  color,
  variant,
  overflowColor,
  overflowVariant,
  showTooltip = false,
  tooltipEnterDelayMs,
  onRootClickCapture,
  chipSx,
  labelSx,
  overflowChipSx,
  tooltipContent,
}: Omit<Props, 'wrap'>) {
  const resolvedChipSx = { ...CHIP_SX, ...chipSx };
  const resolvedLabelSx = { ...LABEL_SX, ...labelSx };
  const resolvedOverflowChipSx = { ...OVERFLOW_CHIP_SX, ...overflowChipSx };
  const resolvedOverflowColor = overflowColor ?? color ?? 'default';
  const resolvedOverflowVariant = overflowVariant ?? variant ?? 'soft';

  const [visibleCount, setVisibleCount] = useState(tags.length);

  const chipsContainerRef = useRef<HTMLDivElement | null>(null);
  const overflowMeasureRef = useRef<HTMLSpanElement | null>(null);
  const chipMeasureRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const { width: containerWidth } = useElementDimensions(`${containerIdentifier}`);

  useLayoutEffect(() => {
    chipMeasureRefs.current.length = tags.length;

    if (!containerWidth || containerWidth <= 0) {
      setVisibleCount(tags.length);
      return;
    }

    const GAP_WIDTH = 4;
    const availableWidth = chipsContainerRef.current?.clientWidth ?? 0;

    if (!availableWidth || tags.length === 0) {
      setVisibleCount(tags.length);
      return;
    }

    // Use intrinsic content width to avoid post-truncation reflow changing fit decisions.
    const chipWidths = tags.map((_, i) => {
      const el = chipMeasureRefs.current[i];
      return el?.getBoundingClientRect().width ?? 0;
    });

    const getOverflowWidth = (hiddenCount: number) => {
      if (overflowMeasureRef.current) {
        overflowMeasureRef.current.textContent = `+${hiddenCount}`;
      }
      return overflowMeasureRef.current?.getBoundingClientRect().width ?? 0;
    };

    // Build visible chips left-to-right while reserving +x width whenever items remain.
    let nextVisibleCount = 0;
    let usedWidth = 0;
    for (let i = 0; i < chipWidths.length; i += 1) {
      const nextChipWidth = chipWidths[i];
      const chipGap = nextVisibleCount > 0 ? GAP_WIDTH : 0;
      const nextUsedWidth = usedWidth + chipGap + nextChipWidth;
      const remainingAfter = chipWidths.length - (i + 1);

      let requiredWidth = nextUsedWidth;
      if (remainingAfter > 0) {
        requiredWidth += GAP_WIDTH + getOverflowWidth(remainingAfter);
      }

      if (requiredWidth <= availableWidth || nextVisibleCount === 0) {
        nextVisibleCount += 1;
        usedWidth = nextUsedWidth;
      } else {
        break;
      }
    }

    setVisibleCount(nextVisibleCount);
  }, [tags, containerWidth]);

  const nOfHiddenChips = Math.max(0, tags.length - visibleCount);

  const Wrapper = useCallback(
    ({ children }: { children: ReactElement }) => {
      if (showTooltip) {
        return (
          <Tooltip
            enterDelay={tooltipEnterDelayMs}
            placement="left-start"
            slotProps={{
              tooltip: {
                sx: (theme) => ({
                  maxWidth: TOOLTIP_MAX_WIDTH,
                  p: 0,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  border: `solid 1px ${theme.palette.divider}`,
                  boxShadow: theme.customShadows.dropdown,
                }),
              },
            }}
            title={tooltipContent ?? (
              <TagsListTooltipContent tags={tags} color={color} variant={variant} />
            )}
          >
            {children}
          </Tooltip>
        );
      }
      return <>{children}</>;
    },
    [showTooltip, tags, tooltipEnterDelayMs, tooltipContent, color, variant]
  );

  return (
    <Wrapper>
      <Box
        onClickCapture={onRootClickCapture}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.5,
          flexGrow: 1,
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Label
          ref={overflowMeasureRef}
          variant={resolvedOverflowVariant}
          color={resolvedOverflowColor}
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            ...resolvedOverflowChipSx,
          }}
        >
          +0
        </Label>
        <Box
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            height: 0,
            overflow: 'hidden',
          }}
        >
          {tags.map((c, i) => (
            <Label
              key={`measure-${c.id}-${i}`}
              variant={c.variant ?? variant ?? 'soft'}
              color={c.color ?? color ?? 'default'}
              sx={{ minWidth: 50, ...CHIP_LAYOUT_SX }}
              ref={(el: HTMLSpanElement | null) => {
                chipMeasureRefs.current[i] = el;
              }}
            >
              <Box component="span" sx={{ display: 'inline-block', lineHeight: 1.2 }}>
                {c.label}
              </Box>
            </Label>
          ))}
        </Box>
        <Box
          ref={chipsContainerRef}
          sx={{
            display: 'flex',
            gap: 0.5,
            overflow: 'hidden',
            flexWrap: 'nowrap',
            alignItems: 'center',
            height: '100%',
            alignContent: 'center',
            width: '100%',
          }}
        >
          {tags?.map((c, i) => {
            const isVisible = i < visibleCount;

            return (
            <Box
              key={`chip-${String(c.id)}-${i}`}
              sx={{
                display: isVisible ? 'flex' : 'none',
                visibility: isVisible ? 'visible' : 'hidden',
                pointerEvents: isVisible ? 'auto' : 'none',
                order: 0,
                minWidth: i === 0 ? 0 : 'auto',
                flexShrink: i === 0 ? 1 : 0,
                maxWidth: i === 0 ? '100%' : 'none',
                overflow: 'hidden',
              }}
            >
              <Label
                variant={c.variant ?? variant ?? 'soft'}
                color={c.color ?? color ?? 'default'}
                flex={1}
                sx={{
                  minWidth: 50,
                  ...resolvedChipSx,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    lineHeight: 1.2,
                    minWidth: 0,
                    maxWidth: '100%',
                    ...resolvedLabelSx,
                  }}
                >
                  {c.label}
                </Box>
              </Label>
            </Box>
            );
          })}
          {nOfHiddenChips > 0 && (
            <Label
              variant={resolvedOverflowVariant}
              color={resolvedOverflowColor}
              sx={{
                order: 1,
                flexGrow: 0,
                flexShrink: 0,
                alignSelf: 'center',
                pointerEvents: 'none',
                ...resolvedOverflowChipSx,
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                +{nOfHiddenChips}
              </Box>
            </Label>
          )}
        </Box>
      </Box>
    </Wrapper>
  );
}

export const TagsList = ({ wrap = false, ...props }: Props) =>
  wrap ? <TagsListWrapped {...props} /> : <TagsListOverflow {...props} />;
